import React from 'react';
import PropTypes from 'prop-types';
import Loader from '../Loader';
import NoticeBar from '../NoticeBar';
import ContentContainer from '../ContentContainer';
import history from '../../history';
import API from '../../API';
import Isvg from 'react-inlinesvg';
import StaticPenLegend from './StaticPenLegend';
import LocaleText from '../LocaleText';

class StaticPen extends React.Component {
    constructor() {
        super();
        this.renderWritingColors = this.renderWritingColors.bind(this);
        this.renderPrintableAreas = this.renderPrintableAreas.bind(this);
        this.renderCheckoutButton = this.renderCheckoutButton.bind(this);
        this.onCheckout = this.onCheckout.bind(this);
        this.equalizeDescriptionHeight = this.equalizeDescriptionHeight.bind(this);
        this.state = {equalizedContainerHeight: null}
    }

    componentDidMount() {
        const slug = this.props.match.params.pen;
        API.getStaticPenBySlug(slug).then(res => {
            this.props.setPen(res.data.data);
            this.props.setIsLoading(false);
        });
    }

    componentDidUpdate() {
        this.equalizeDescriptionHeight();
    }

    equalizeDescriptionHeight() {
        let descriptionContainer = this.descriptionContainer;
        let printableAreasContainer = this.printableAreasContainer;

        if (descriptionContainer && printableAreasContainer) {
            let heightMax = Math.max(descriptionContainer.clientHeight, printableAreasContainer.clientHeight);

            if (this.state.equalizedContainerHeight !== heightMax) {
                this.setState({equalizedContainerHeight: heightMax});
            }
        }
    }

    componentWillUnmount() {
        this.props.setIsLoading(true);
        this.props.closeLegend();
    }

    renderPrintableAreas() {
        const { pen } = this.props;
        return (
            <table className="StaticPen__printable-areas">
                <tbody>
                    {Object.keys(pen.printable_areas).map(area => {
                        return (
                            <tr key={area}>
                                <td>{area}</td>
                                <td>{pen.printable_areas[area]}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }

    renderWritingColors() {
        const { pen } = this.props;
        return (
            <div className="StaticPen__writing-colors">
                {pen.writing_colors.map(color => (
                    <div
                        className="StaticPen__writing-color"
                        style={{ backgroundColor: color }}
                        key={color}
                    />
                ))}
            </div>
        );
    }

    onCheckout() {
        const { locale } = this.props;
        this.props.setCheckoutBehaviour('CONTACT_DETAILS_ONLY');
        this.props.setStep(1);
        history.push(`/${locale}/checkout`);
    }

    renderCheckoutButton() {
        return (
            <button className="btn is-primary is-bigger" onClick={this.onCheckout}>
                <LocaleText textKey="static.pen.checkout.button.label" />
            </button>
        );
    }

    render() {
        const { isLoading, isLegendOpen, openLegend, closeLegend, locale } = this.props;
        const { pen } = this.props;
        const classes = ['StaticPen', ...this.props.classes];

        if (isLoading) {
            classes.push('is-loading');
            return (
                <div className={classes.join(' ')}>
                    <Loader />
                </div>
            );
        }

        if (isLegendOpen) {
            classes.push('is-fixed');
        }

        const sectionTitles = {
            description: <LocaleText textKey="static.pen.description.title" />,
            printable_areas: <LocaleText textKey="static.pen.printable.areas.title" />,
            showcase: <LocaleText textKey="static.pen.showcase.title" />,
            minimum_order_amount: <LocaleText textKey="static.pen.minimum.order.amount.title" />,
            casing: <LocaleText textKey="static.pen.casing.title" />,
            writing_colors: <LocaleText textKey="static.pen.writing.colors.title" />,
        };

        return (
            <div className={classes.join(' ')}>
                <NoticeBar
                    icon={<Isvg src="../../svg/attention.svg" />}
                    notice={<LocaleText textKey="static.pen.notice" />}
                />
                <ContentContainer>
                    <div className="StaticPen__head">
                        <h1 className="StaticPen__title">{pen.name}</h1>
                        {this.renderCheckoutButton()}
                    </div>

                    <div className="StaticPen__grid">
                        <div className="StaticPen__column">
                            <section className="StaticPen__section StaticPen__section--description"
                                     ref={element => this.descriptionContainer = element}
                                     style={{ height: this.state.equalizedContainerHeight}}>
                                <h2 className="StaticPen__section-title">
                                    {sectionTitles.description}
                                </h2>
                                <p>{pen.description}</p>
                            </section>
                            <section className="StaticPen__section StaticPen__section--casing">
                                <h2 className="StaticPen__section-title">{sectionTitles.casing}</h2>
                                <img src={pen.casing.image} alt="" />
                            </section>
                            <section className="StaticPen__section StaticPen__section--minimum-order-amount">
                                <h2 className="StaticPen__section-title">
                                    {sectionTitles.minimum_order_amount}{' '}
                                    <span onClick={openLegend}>
                                        <Isvg src="../../svg/info.svg" />
                                    </span>
                                </h2>
                                <img
                                    src={pen.minimum_order_amount.images.desktop}
                                    className="desktop-image"
                                    alt=""
                                />
                                <img
                                    src={pen.minimum_order_amount.images.mobile}
                                    className="mobile-image"
                                    alt=""
                                />
                            </section>
                        </div>
                        <div className="StaticPen__column">
                            <section className="StaticPen__section StaticPen__section--printable-areas"
                                     ref={element => this.printableAreasContainer = element}
                                     style={{ height: this.state.equalizedContainerHeight}}>
                                <h2 className="StaticPen__section-title">
                                    {sectionTitles.printable_areas}
                                </h2>
                                {this.renderPrintableAreas()}
                            </section>
                            <section className="StaticPen__section StaticPen__section--showcase">
                                <h2 className="StaticPen__section-title">
                                    {sectionTitles.showcase}
                                </h2>
                                <img src={pen.image} alt="" />
                            </section>
                            <section className="StaticPen__section StaticPen__section--writing-colors">
                                <h2 className="StaticPen__section-title">
                                    {sectionTitles.writing_colors}
                                </h2>
                                {this.renderWritingColors()}
                            </section>
                        </div>
                    </div>
                    <div className="StaticPen__footer">{this.renderCheckoutButton()}</div>
                </ContentContainer>

                <StaticPenLegend isOpen={isLegendOpen} onClose={closeLegend} locale={locale} />
            </div>
        );
    }
}

StaticPen.propTypes = {
    classes: PropTypes.arrayOf(PropTypes.string),
    isLegendOpen: PropTypes.bool,
    closeLegend: PropTypes.func.isRequired,
    openLegend: PropTypes.func.isRequired,
};

StaticPen.defaultProps = {
    classes: [],
    isLegendOpen: false,
};

export default StaticPen;
